import { useMemo } from 'react'

import { type TeamMember } from '@api'
import { useUnit } from 'effector-react'

import { ReassignDropdown } from '@/components/layout/reassign-dropdown.tsx'
import { Tooltip } from '@/components/ui/tooltip'
import { $ableToReassignSales } from '@/models/feature-flags'
import { $hasReassignPermission } from '@/models/permissions.ts'
import type { Notification } from '@/modules/chatting/types'

import { useReassignNotification } from './useReassignNotification'

interface Props {
  notification: Notification
  previousNotification?: Notification
  members: TeamMember[]
  isMembersFetching: boolean
}

export const NotificationsReassignDropdown = ({
  members,
  isMembersFetching,
  notification,
  previousNotification,
}: Props) => {
  const { onReassign, isLoading } = useReassignNotification({
    notification,
    previousNotification,
  })

  const hasPermission = useUnit($hasReassignPermission)
  const hasFeatureFlag = useUnit($ableToReassignSales)

  const {
    type,
    assignedUserId,
    originallyAssignedUserId,
    createdAt,
    browserName,
  } = notification

  const assignee =
    members?.find((member) => member.userId === assignedUserId)?.name ??
    browserName ??
    'no one'
  const canReassign =
    !isMembersFetching && !!members.length && hasPermission && hasFeatureFlag
  const isAssigned = !!assignedUserId || !!originallyAssignedUserId
  const isSale = type === 'paided_message' || type === 'tip'
  const isRecentlyCreated = Date.now() - Date.parse(createdAt) < 10 * 60 * 1000
  const isWaitingToBeAttributed = isRecentlyCreated && !isAssigned
  const canDropdownOpen = canReassign && !isWaitingToBeAttributed && !isLoading

  const trigger = useMemo(() => {
    if (isWaitingToBeAttributed) {
      return (
        <Tooltip
          content="Sales are usually attributed within less than 10 minutes."
          contentClassName="min-w-max"
          className="whitespace-nowrap"
        >
          Not attributed yet
        </Tooltip>
      )
    }

    return (
      <span className="overflow-hidden text-ellipsis">
        {type === 'paided_message' ? 'Sent by' : 'Collected by'} {assignee}
      </span>
    )
  }, [assignee, isWaitingToBeAttributed, type])

  if (!isAssigned && !isSale) return null

  return (
    <ReassignDropdown
      handleChangeAssignee={onReassign}
      assignedUserId={assignedUserId}
      originallyAssignedUserId={originallyAssignedUserId}
      canDropdownOpen={canDropdownOpen}
      trigger={trigger}
      members={members}
      isLoading={isLoading}
    />
  )
}
